import {makeAutoObservable} from 'mobx';
import moment from 'moment';
import _ from 'lodash';
import * as Sentry from '@sentry/react';
import {DomainStore} from 'shared/store';
import {endpoints, types} from 'main/tools';
import CalendarEventViewModel from './CalendarEventViewModel';
import {buildRows} from './eventGroups';

const EVENT_TYPES = ['time_off_request', 'holiday', 'birthday', 'anniversary', 'first_day'];
const UPCOMING_WINDOW_DAYS = 7;
const MAX_UPCOMING_DAYS = 4;
const ALL_DAY_TYPE_ORDER = ['holiday', 'birthday', 'anniversary', 'first_day', 'time_off_request'];

class WhatsGoingOnState {
  store = new DomainStore();

  emojiReady;
  events = [];

  constructor() {
    makeAutoObservable(this, {
      store: false,
      emojiReady: false
    });
  }

  receiveProps({emojiReady}) {
    this.emojiReady = emojiReady;
  }

  get enabled() {
    return new URLSearchParams(window.location.search).get('whats_going_on') === 'true';
  }

  async load() {
    if (!this.enabled) return;

    const startDate = moment().startOf('day');
    const endDate = startDate.clone().add(UPCOMING_WINDOW_DAYS, 'days');

    const [response] = await this.store._compose([
      endpoints.CALENDAR_EVENTS.WITH_TYPES.with(
        startDate.format('YYYY-MM-DD'),
        endDate.format('YYYY-MM-DD'),
        EVENT_TYPES
      )
    ], {failSilently: true});

    if (_.get(response, 'failedSilently')) {
      Sentry.captureException(response.e);
      return;
    }

    this.events = this.store._getAll(types.CALENDAR_EVENT)
      .map(event => new CalendarEventViewModel(event));
  }

  get todaysAgenda() {
    return this.agendaFor(moment().startOf('day'), 'today');
  }

  get upcomingAgendas() {
    const today = moment().startOf('day');

    return _.chain(_.range(1, UPCOMING_WINDOW_DAYS + 1))
      .map(offset => this.agendaFor(today.clone().add(offset, 'days'), 'upcoming'))
      .filter('hasEvents')
      .take(MAX_UPCOMING_DAYS)
      .value();
  }

  agendaFor(date, scope) {
    const events = _.filter(this.events, event => event.occursOn(date));
    const key = date.format('YYYY-MM-DD');

    return {
      key,
      date,
      hasEvents: !_.isEmpty(events),
      rows: buildRows(this.sortedForDisplay(events), scope, key)
    };
  }

  sortedForDisplay(events) {
    return [
      ..._.sortBy(_.filter(events, 'allDay'), [event => _.indexOf(ALL_DAY_TYPE_ORDER, event.type), 'title']),
      ..._.sortBy(_.reject(events, 'allDay'), [event => event.start.valueOf(), 'title'])
    ];
  }
}

export default WhatsGoingOnState;
